"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { motion } from "framer-motion";
import Logo from "../layout/Logo";
import { Check, Copy, Instagram, Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getPublicLocaleFromPathname,
  localizePublicHref,
} from "@/lib/i18n/public-routes";

const column = {
  hidden: { opacity: 0, y: 12 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.05 * i, duration: 0.35, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

const aiPromptLink =
  "https://chatgpt.com/?prompt=As+a+job+seeker%2C+I+want+to+understand+why+Landfast+is+the+best+resume+and+cover+letter+co-pilot+for+tailoring+applications+quickly.+Use+the+information+from+https%3A%2F%2Flandfast.app+to+summarize+the+top+features+and+advantages+that+help+me+get+more+interviews%2C+pass+ATS+filters%2C+and+save+time+applying.";

const applicationLinks = [
  { labelKey: "FOOTER_APPLICATION_BLOGS_LINK_TEXT", href: "/blog" },
  { labelKey: "FOOTER_APPLICATION_FEATURES_LINK_TEXT", href: "/resume-builder" },
  { labelKey: "FOOTER_APPLICATION_PRICING_LINK_TEXT", href: "/pricing" },
  { labelKey: "FOOTER_APPLICATION_APP_LINK_TEXT", href: "/sign-in" },
];

const toolsLinks = [
  {
    labelKey: "FOOTER_TOOLS_RESUME_BUILDER_LINK_TEXT",
    href: "/resume-builder",
  },
  {
    labelKey: "FOOTER_TOOLS_JOB_TRACKER_LINK_TEXT",
    href: "/job-finder",
  },
  {
    labelKey: "FOOTER_TOOLS_COVER_LETTER_LINK_TEXT",
    href: "/cover-letter-generator",
  },
  {
    labelKey: "FOOTER_TOOLS_AI_INTERVIEWS_LINK_TEXT",
    href: "/interview-prep",
  },
  { labelKey: "FOOTER_TOOLS_AI_RESUME_TRANSLATOR", href: "/resume-builder" },
];

const resumeExamplesLinks = [
  { labelKey: "FOOTER_RESUME_EXAMPLES_ALL_LINK_TEXT", href: "/resume-examples" },
  {
    labelKey: "FOOTER_RESUME_EXAMPLES_SOFTWARE_ENGINEER_LINK_TEXT",
    href: "/resume-examples/software-engineer",
  },
  {
    labelKey: "FOOTER_RESUME_EXAMPLES_PRODUCT_MANAGER_LINK_TEXT",
    href: "/resume-examples/product-manager",
  },
  {
    labelKey: "FOOTER_RESUME_EXAMPLES_DATA_ANALYST_LINK_TEXT",
    href: "/resume-examples/data-analyst",
  },
  {
    labelKey: "FOOTER_RESUME_EXAMPLES_UX_DESIGNER_LINK_TEXT",
    href: "/resume-examples/ux-designer",
  },
  {
    labelKey: "FOOTER_RESUME_EXAMPLES_DIGITAL_MARKETING_MANAGER_LINK_TEXT",
    href: "/resume-examples/digital-marketing-manager",
  },
  {
    labelKey: "FOOTER_RESUME_EXAMPLES_CUSTOMER_SUCCESS_MANAGER_LINK_TEXT",
    href: "/resume-examples/customer-success-manager",
  },
  {
    labelKey: "FOOTER_RESUME_EXAMPLES_ACCOUNTANT_LINK_TEXT",
    href: "/resume-examples/accountant",
  },
];

const supportLinks = [
  { labelKey: "FOOTER_SUPPORT_FAQ_LINK_TEXT", href: "/#faq" },
  { labelKey: "FOOTER_SUPPORT_PRIVACY_LINK_TEXT", href: "/privacy" },
  {
    labelKey: "Contact",
    href: "mailto:contact@landfast.app",
    staticLabel: "contact@landfast.app",
  },
];

export const FooterSection = () => {
  const { t } = useTranslation();
  const [emailCopyStatus, setEmailCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const pathname = usePathname() ?? "/";
  const publicLocale = getPublicLocaleFromPathname(pathname);
  const year = new Date().getFullYear();
  const localizedHref = (href: string) => localizePublicHref(publicLocale, href);
  const copyContactEmail = async () => {
    try {
      await navigator.clipboard.writeText("contact@landfast.app");
      setEmailCopyStatus("copied");
    } catch {
      setEmailCopyStatus("error");
    }
  };

  return (
    <footer className="relative left-1/2 w-screen max-w-none -translate-x-1/2 overflow-hidden rounded-t-[3rem] rounded-b-none border border-b-0 border-white/40 bg-white/75 shadow-[0_60px_160px_-80px_rgba(15,23,42,0.7)] backdrop-blur-2xl dark:border-white/10 dark:bg-white/10">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-10 h-72 w-72 rounded-full bg-[radial-gradient(circle_at_center,_rgba(129,140,248,0.28),_rgba(255,255,255,0))] blur-3xl opacity-90 dark:bg-[radial-gradient(circle_at_center,_rgba(129,140,248,0.35),_rgba(15,23,42,0))]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 left-20 h-80 w-80 rounded-full bg-[radial-gradient(circle_at_center,_rgba(56,189,248,0.25),_rgba(255,255,255,0))] blur-3xl opacity-80 dark:bg-[radial-gradient(circle_at_center,_rgba(14,165,233,0.3),_rgba(15,23,42,0))]"
      />

      {/* Top */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.4 }}
        className="relative mx-auto max-w-7xl px-6 py-12 sm:px-8 lg:px-10"
      >
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_1.6fr]">
          {/* Left */}
          <motion.div
            variants={column}
            initial="hidden"
            whileInView="show"
            className="flex h-full flex-col gap-8"
          >
            <div className="flex flex-col items-center text-center md:items-start md:text-left">
              <Logo href={localizedHref("/")} variant="wordmark" size={64} className="-ml-3" />
              <p className="mt-3 max-w-sm text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t("FOOTER_TAGLINE_TEXT")}
              </p>
              <button
                type="button"
                onClick={copyContactEmail}
                title={t(emailCopyStatus === "copied" ? "FOOTER_EMAIL_COPIED" : "FOOTER_EMAIL_COPY_HINT")}
                aria-label={`${t("FOOTER_CONTACT_EMAIL")}. ${t(emailCopyStatus === "copied" ? "FOOTER_EMAIL_COPIED" : "FOOTER_EMAIL_COPY_HINT")}`}
                className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-xl border border-white/50 bg-white/45 px-3 py-2 text-sm font-medium text-slate-600 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-white/80 hover:text-indigo-700 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-300 dark:hover:border-sky-200/30 dark:hover:bg-white/10 dark:hover:text-sky-200 dark:focus-visible:ring-offset-slate-900"
              >
                <span>{t("FOOTER_CONTACT_EMAIL")}</span>
                {emailCopyStatus === "copied" ? (
                  <Check aria-hidden="true" className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
                ) : (
                  <Copy aria-hidden="true" className="h-4 w-4" />
                )}
                <span className="sr-only" role="status" aria-live="polite">
                  {emailCopyStatus === "copied"
                    ? t("FOOTER_EMAIL_COPIED")
                    : emailCopyStatus === "error"
                      ? t("FOOTER_EMAIL_COPY_FAILED")
                      : ""}
                </span>
              </button>

            </div>
            <div className="mt-auto w-full max-w-sm self-center rounded-2xl border border-white/30 bg-white/50 px-5 py-5 text-center text-sm text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-200 md:self-start md:text-left">
              <p className="font-semibold text-slate-800 dark:text-white">
                {t("FOOTER_AI_PROMPT_TITLE_TEXT")}
              </p>
              <Link
                href={aiPromptLink}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center justify-center text-xs font-semibold uppercase tracking-wide text-indigo-600 transition hover:text-indigo-400 dark:text-sky-300 dark:hover:text-sky-200"
              >
                {t("FOOTER_AI_PROMPT_CTA_TEXT")}
              </Link>
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.nav
            variants={column}
            initial="hidden"
            whileInView="show"
            custom={1}
            aria-label="Footer navigation"
            className="relative rounded-[2.5rem] border border-white/40 bg-white/60 px-6 py-8 backdrop-blur dark:border-white/10 dark:bg-white/10 sm:px-8 lg:px-10"
          >
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
              {/* Application */}
              <div>
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-white">
                  {t("FOOTER_APPLICATION_TITLE_TEXT")}
                </p>
                <ul className="space-y-2">
                  {applicationLinks.map((l) => (
                    <li key={l.labelKey}>
                      <Link
                        href={localizedHref(l.href)}
                        className="text-sm text-slate-600 transition hover:text-indigo-600 dark:text-slate-300 dark:hover:text-sky-200"
                      >
                        {t(l.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tools */}
              <div>
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-white">
                  {t("FOOTER_TOOLS_TITLE_TEXT")}
                </p>
                <ul className="space-y-2">
                  {toolsLinks.map((l) => (
                    <li key={l.labelKey}>
                      <Link
                        href={localizedHref(l.href)}
                        className="text-sm text-slate-600 transition hover:text-indigo-600 dark:text-slate-300 dark:hover:text-sky-200"
                      >
                        {t(l.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resume Examples */}
              <div>
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-white">
                  {t("FOOTER_RESUME_EXAMPLES_TITLE_TEXT")}
                </p>
                <ul className="space-y-2">
                  {resumeExamplesLinks.map((l) => (
                    <li key={l.labelKey}>
                      <Link
                        href={localizedHref(l.href)}
                        className="text-sm text-slate-600 transition hover:text-indigo-600 dark:text-slate-300 dark:hover:text-sky-200"
                      >
                        {t(l.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Support */}
              <div>
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-white">
                  {t("FOOTER_SUPPORT_TITLE_TEXT")}
                </p>
                <ul className="space-y-2">
                  {supportLinks.map((l) => (
                    <li key={l.labelKey}>
                      <Link
                        href={
                          l.href.startsWith("mailto:")
                            ? l.href
                            : localizedHref(l.href)
                        }
                        className="text-sm text-slate-600 transition hover:text-indigo-600 dark:text-slate-300 dark:hover:text-sky-200"
                      >
                        {l.labelKey ? t(l.labelKey) : l.staticLabel}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.nav>
        </div>
      </motion.div>

      {/* Bottom */}
      <div className="relative border-t border-white/40 dark:border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-6 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 items-center gap-5 text-center text-sm text-slate-500 dark:text-slate-300 md:grid-cols-3 md:text-left">
            <p className="md:justify-self-start">
              © {year} LandFast. {t("FOOTER_COPYRIGHT_TEXT")}
            </p>
            <div className="flex items-center justify-center gap-5">
              <Link
                href={localizedHref("/terms")}
                className="transition-colors hover:text-indigo-600 dark:hover:text-sky-200"
              >
                {t("FOOTER_BOTTOM_TERMS_LINK_TEXT")}
              </Link>
              <Link
                href={localizedHref("/privacy")}
                className="transition-colors hover:text-indigo-600 dark:hover:text-sky-200"
              >
                {t("FOOTER_BOTTOM_PRIVACY_LINK_TEXT")}
              </Link>
            </div>
            <div aria-label="Social media" className="flex items-center justify-center gap-3 md:justify-self-end">
              <a
                href="https://www.linkedin.com/company/landfast-app/"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/70 text-slate-500 shadow-sm transition duration-200 hover:-translate-y-1 hover:scale-105 hover:border-[#0A66C2] hover:bg-[#0A66C2] hover:text-white hover:shadow-lg hover:shadow-blue-900/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A66C2] focus-visible:ring-offset-2 dark:border-white/10 dark:bg-white/10 dark:text-slate-300 dark:hover:border-[#0A66C2] dark:hover:bg-[#0A66C2] dark:hover:text-white"
              >
                <Linkedin aria-hidden="true" className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
              </a>
              <a
                href="https://www.instagram.com/landfast.app/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/70 text-slate-500 shadow-sm transition duration-200 hover:-translate-y-1 hover:scale-105 hover:border-fuchsia-500 hover:bg-gradient-to-br hover:from-amber-400 hover:via-pink-500 hover:to-violet-600 hover:text-white hover:shadow-lg hover:shadow-pink-900/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2 dark:border-white/10 dark:bg-white/10 dark:text-slate-300 dark:hover:border-pink-500 dark:hover:text-white"
              >
                <Instagram aria-hidden="true" className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
              </a>
              <a
                href="https://www.tiktok.com/@landfast.app"
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/70 text-slate-500 shadow-sm transition duration-200 hover:-translate-y-1 hover:scale-105 hover:border-slate-900 hover:bg-slate-900 hover:text-white hover:shadow-lg hover:shadow-slate-900/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-800 focus-visible:ring-offset-2 dark:border-white/10 dark:bg-white/10 dark:text-slate-300 dark:hover:border-black dark:hover:bg-black dark:hover:text-white"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5 transition-transform duration-200 group-hover:scale-110"
                  aria-hidden="true"
                  focusable="false"
                  fill="currentColor"
                >
                  <path d="M14.8 3c.4 1.6 1.6 2.8 3.2 3.1v2.5c-1.5 0-2.8-.5-3.8-1.4v6.1c0 3.1-2.5 5.7-5.7 5.7S2.8 16.3 2.8 13.2c0-3.1 2.5-5.7 5.7-5.7.4 0 .8 0 1.2.1v2.7c-.4-.2-.8-.2-1.2-.2-1.6 0-2.9 1.3-2.9 2.9S6.9 16 8.5 16s2.9-1.3 2.9-2.9V3h3.4z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
